<template>
    <vx-modal :visible="props.visible" padding="standard" size="md" @update:visible="emit('update:visible', $event)">
        <template v-slot:header>Set to Draft</template>

        <template v-slot:subheader> The user who created this campaign will be notified via email. </template>

        <div class="flex flex-col gap-8">
            <vx-alert v-if="props.campaign.owner" :closeable="false">
                <template v-slot:icon>
                    <font-awesome-icon :icon="faEnvelope" />
                </template>
                <span class="font-bold">{{ props.campaign.owner.name }} &lt;{{ props.campaign.owner.email }}&gt;</span>
                will be notified.
            </vx-alert>

            <div class="flex flex-col gap-1.5">
                <div class="flex items-start flex-col gap-4">
                    <vx-badge color="violet" size="xs">
                        <span class="pb-[1.5px] text-xs leading-snug">Optional</span>
                        <font-awesome-icon widthAuto :icon="faPencilSlash" size="xs" />
                    </vx-badge>
                    <span class="font-bold text-base text-slate-700">Custom Message</span>
                </div>
                <vx-input
                    v-model="formData.customMessage"
                    data-test="custom-message"
                    :maxlength="1000"
                    :counter="true"
                    :rows="4"
                />
                <p v-if="displayFirstError(validation.customMessage)" class="text-xs text-red-500 mt-1">
                    {{ displayFirstError(validation.customMessage) }}
                </p>
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 justify-between lg:flex-row">
                <vx-button @click="emit('update:visible', false)" color="muted" size="lg" data-test="cancel-btn">
                    Cancel
                </vx-button>
                <vx-button
                    @click="submit"
                    color="primary"
                    :loading="saving"
                    :disabled="saving"
                    size="lg"
                    class="grow"
                    data-test="set-to-draft-btn"
                >
                    Set to Draft
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup lang="ts">
import { VxModal, VxButton, VxAlert, VxBadge, VxInput } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faEnvelope, faPencilSlash } from '@fortawesome/pro-solid-svg-icons';
import { ref, watch } from 'vue';
import useVuelidate from '@vuelidate/core';
import { maxLength } from '@vuelidate/validators';
import { displayFirstError, validateRules } from '~/utils/validation';
import blastForecastClient from '~/services/blastForecast/blastForecastClient';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';
import type { BlastForecastCampaignsResponse } from '~/services/blastForecast/blastForecastClient.types';

const teamId = Spark.state.currentTeam.id;

const props = defineProps<{
    visible: boolean;
    campaign: BlastForecastCampaignsResponse['items'][0];
    orgUnitId?: number;
}>();

const emit = defineEmits<{
    (e: 'update:visible', value: boolean): void;
    (e: 'drafted'): void;
}>();

const toasted = useToasted();

const saving = ref(false);
const formData = ref({ customMessage: '' });

const validation = useVuelidate(
    {
        customMessage: {
            maxLength: maxLength(1000),
        },
    },
    formData
);

watch(
    () => props.visible,
    (visible) => {
        if (visible) {
            formData.value.customMessage = '';
            validation.value.$reset();
        }
    }
);

const submit = async () => {
    const isValid = await validateRules(validation);
    if (saving.value || !isValid) {
        return;
    }

    saving.value = true;

    try {
        const context = { teamId, orgUnitId: props.orgUnitId };
        await blastForecastClient.adjustCampaign(context, props.campaign.id, {
            status: 'draft',
            custom_message: formData.value.customMessage.trim() || undefined,
        });

        toasted.success('Campaign set to draft');
        emit('drafted');
        emit('update:visible', false);
    } catch (error) {
        toasted.error(catchRequestError(error));
    } finally {
        saving.value = false;
    }
};
</script>
